import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import backofficeService from '../../../services/backofficeService';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import GamePicker from './GamePicker';
import './BetForm.css';

const emptyGame = {
  home_team: '',
  away_team: '',
  sport: 'futebol',
  pick: '',
  pick_description: '',
  game_odd: '',
  date: new Date().toISOString().split('T')[0],
  time: '12:00',
  competition: '',
  result: 'pending',
  extra_picks: [],
  espn_event_id: null,
};

const emptyExtraPick = {
  pick: '',
  pick_description: '',
  odd: '',
};

// Odd combinada do jogo: odd do pick principal x odds das seleções extra (Bet Builder)
const getCombinedOdd = (game) => {
  const base = parseFloat(game.game_odd) || 1;
  return (game.extra_picks || []).reduce((acc, extra) => acc * (parseFloat(extra.odd) || 1), base);
};

const BetForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    total_odd: '',
    result: '',
    games: [{ ...emptyGame }],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditing) {
      loadBet();
    }
  }, [id]);

  const loadBet = async () => {
    setIsLoading(true);
    try {
      const bet = await backofficeService.getBet(id);
      setFormData({
        date: bet.date || '',
        amount: bet.amount || '',
        total_odd: bet.total_odd || '',
        result: bet.result || '',
        games: bet.games?.length > 0
          ? bet.games.map(game => ({ ...game, extra_picks: game.extra_picks || [] }))
          : [{ ...emptyGame }],
      });
    } catch (error) {
      setError('Erro ao carregar bet');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGameChange = (index, field, value) => {
    setFormData(prev => {
      const updatedGames = [...prev.games];
      updatedGames[index] = { ...updatedGames[index], [field]: value };
      return { ...prev, games: updatedGames };
    });
  };

  // Fill a game from an ESPN fixture picked in the search
  const pickEspnGame = (index, event) => {
    setFormData(prev => {
      const updatedGames = [...prev.games];
      updatedGames[index] = {
        ...updatedGames[index],
        home_team: event.home,
        away_team: event.away,
        sport: event.sport,
        date: event.date,
        time: event.time,
        espn_event_id: event.id,
      };
      return { ...prev, games: updatedGames };
    });
  };

  const addGame = () => {
    setFormData(prev => ({
      ...prev,
      games: [...prev.games, { ...emptyGame }],
    }));
  };

  const removeGame = (index) => {
    if (formData.games.length === 1) return;
    setFormData(prev => ({
      ...prev,
      games: prev.games.filter((_, i) => i !== index),
    }));
  };

  const addExtraPick = (gameIndex) => {
    setFormData(prev => {
      const updatedGames = [...prev.games];
      const game = updatedGames[gameIndex];
      updatedGames[gameIndex] = { ...game, extra_picks: [...(game.extra_picks || []), { ...emptyExtraPick }] };
      return { ...prev, games: updatedGames };
    });
  };

  const removeExtraPick = (gameIndex, pickIndex) => {
    setFormData(prev => {
      const updatedGames = [...prev.games];
      const game = updatedGames[gameIndex];
      updatedGames[gameIndex] = {
        ...game,
        extra_picks: game.extra_picks.filter((_, i) => i !== pickIndex),
      };
      return { ...prev, games: updatedGames };
    });
  };

  const handleExtraPickChange = (gameIndex, pickIndex, field, value) => {
    setFormData(prev => {
      const updatedGames = [...prev.games];
      const game = updatedGames[gameIndex];
      const updatedPicks = [...game.extra_picks];
      updatedPicks[pickIndex] = { ...updatedPicks[pickIndex], [field]: value };
      updatedGames[gameIndex] = { ...game, extra_picks: updatedPicks };
      return { ...prev, games: updatedGames };
    });
  };

  const calculateTotalOdd = () => {
    const total = formData.games.reduce((acc, game) => acc * getCombinedOdd(game), 1);
    setFormData(prev => ({ ...prev, total_odd: total.toFixed(2) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const payload = {
        ...formData,
        games: formData.games.map(g => ({
          ...g,
          game_odd: g.game_odd ? parseFloat(g.game_odd) : null,
          extra_picks: (g.extra_picks || []).map((extra, index) => ({
            pick: extra.pick,
            pick_description: extra.pick_description,
            odd: extra.odd ? parseFloat(extra.odd) : null,
            order: index,
          })),
        })),
      };

      if (isEditing) {
        await backofficeService.updateBet(id, payload);
      } else {
        await backofficeService.createBet(payload);
      }
      navigate('/backoffice/bets');
    } catch (err) {
      setError(err.message || 'Erro ao guardar bet');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="backoffice-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>A carregar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="backoffice-page bet-form-page">
      <div className="page-header">
        <h1>{isEditing ? 'Editar Bet' : 'Nova Bet'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        {error && <div className="form-error">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Data</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="amount">Montante</label>
            <input
              type="text"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Ex: 10€"
            />
          </div>

          <div className="form-group">
            <label htmlFor="total_odd">Odd Total</label>
            <div className="input-with-button">
              <input
                type="text"
                id="total_odd"
                name="total_odd"
                value={formData.total_odd}
                onChange={handleChange}
                placeholder="Ex: 2.50"
              />
              <button type="button" onClick={calculateTotalOdd} className="btn-calc">
                Calcular
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="result">Resultado</label>
            <CustomSelect
              id="result"
              name="result"
              value={formData.result}
              onChange={handleChange}
              options={[
                { value: 'pending', label: 'Pendente' },
                { value: 'won', label: 'Win' },
                { value: 'lost', label: 'Loss' },
                { value: 'void', label: 'Void' },
                { value: 'push', label: 'Push' },
              ]}
            />
          </div>
        </div>

        <div className="games-section">
          <div className="section-header">
            <h3>Jogos ({formData.games.length})</h3>
            <button type="button" onClick={addGame} className="btn-secondary">
              + Adicionar Jogo
            </button>
          </div>

          {formData.games.map((game, index) => (
            <div key={index} className="game-card">
              <div className="game-header">
                <span className="game-number">
                  Jogo {index + 1}
                  {game.extra_picks?.length > 0 && <span className="bb-badge">⛓ Bet Builder</span>}
                </span>
                {formData.games.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeGame(index)}
                    className="btn-remove"
                  >
                    Remover
                  </button>
                )}
              </div>

              <GamePicker
                game={game}
                onPick={(event) => pickEspnGame(index, event)}
                onUnlink={() => handleGameChange(index, 'espn_event_id', null)}
              />

              <div className="form-row">
                <div className="form-group">
                  <label>Equipa Casa</label>
                  <input
                    type="text"
                    value={game.home_team || ''}
                    onChange={(e) => handleGameChange(index, 'home_team', e.target.value)}
                    placeholder="Ex: Porto"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Equipa Fora</label>
                  <input
                    type="text"
                    value={game.away_team || ''}
                    onChange={(e) => handleGameChange(index, 'away_team', e.target.value)}
                    placeholder="Ex: Benfica (opcional)"
                  />
                </div>

                <div className="form-group">
                  <label>Desporto</label>
                  <CustomSelect
                    name="sport"
                    value={game.sport || 'futebol'}
                    onChange={(e) => handleGameChange(index, 'sport', e.target.value)}
                    options={[
                      { value: 'futebol', label: 'Futebol' },
                      { value: 'basket', label: 'Basquetebol' },
                      { value: 'tenis', label: 'Ténis' },
                      { value: 'mma', label: 'MMA' },
                      { value: 'esports', label: 'Esports' },
                      { value: 'cs2', label: 'Counter Strike 2' },
                      { value: 'boxe', label: 'Boxe' },
                      { value: 'futsal', label: 'Futsal' },
                      { value: 'outros', label: 'Outro' },
                    ]}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Pick</label>
                  <input
                    type="text"
                    value={game.pick || ''}
                    onChange={(e) => handleGameChange(index, 'pick', e.target.value)}
                    placeholder="Ex: Over 2.5"
                  />
                </div>

                <div className="form-group">
                  <label>Descrição Pick</label>
                  <input
                    type="text"
                    value={game.pick_description || ''}
                    onChange={(e) => handleGameChange(index, 'pick_description', e.target.value)}
                    placeholder="Ex: Vencedor do jogo"
                  />
                </div>

                <div className="form-group">
                  <label>Odd</label>
                  <input
                    type="number"
                    step="0.01"
                    value={game.game_odd || ''}
                    onChange={(e) => handleGameChange(index, 'game_odd', e.target.value)}
                    placeholder="Ex: 1.85"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Data do Jogo</label>
                  <input
                    type="date"
                    value={game.date || ''}
                    onChange={(e) => handleGameChange(index, 'date', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Hora</label>
                  <input
                    type="time"
                    value={game.time ? game.time.slice(0, 5) : ''}
                    onChange={(e) => handleGameChange(index, 'time', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Competição</label>
                  <CustomSelect
                    name="competition"
                    value={game.competition || ''}
                    onChange={(e) => handleGameChange(index, 'competition', e.target.value)}
                    options={[
                      { value: '', label: '—' },
                      { value: 'Mundial 2026', label: 'Mundial 2026' },
                      { value: 'Champions League', label: 'Champions League' },
                      { value: 'Europa League', label: 'Europa League' },
                      { value: 'Liga Portugal', label: 'Liga Portugal' },
                      { value: 'La Liga', label: 'La Liga' },
                      { value: 'Premier League', label: 'Premier League' },
                      { value: 'Serie A', label: 'Serie A' },
                      { value: 'Bundesliga', label: 'Bundesliga' },
                      { value: 'NBA', label: 'NBA' },
                      { value: 'UFC', label: 'UFC' },
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label>Resultado</label>
                  <CustomSelect
                    name="result"
                    value={game.result || 'pending'}
                    onChange={(e) => handleGameChange(index, 'result', e.target.value)}
                    options={[
                      { value: 'pending', label: 'Pendente' },
                      { value: 'won', label: 'Win' },
                      { value: 'lost', label: 'Loss' },
                      { value: 'void', label: 'Void' },
                    ]}
                  />
                </div>
              </div>

              <div className="bb-picks-block">
                {(game.extra_picks || []).map((extraPick, pickIndex) => (
                  <div key={pickIndex} className="bb-pick-row">
                    <span className="bb-pick-dot"></span>
                    <input
                      type="text"
                      placeholder="Ex: Ambas marcam - Sim"
                      value={extraPick.pick || ''}
                      onChange={(e) => handleExtraPickChange(index, pickIndex, 'pick', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Descrição (opcional)"
                      value={extraPick.pick_description || ''}
                      onChange={(e) => handleExtraPickChange(index, pickIndex, 'pick_description', e.target.value)}
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Odd"
                      value={extraPick.odd || ''}
                      onChange={(e) => handleExtraPickChange(index, pickIndex, 'odd', e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeExtraPick(index, pickIndex)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="bb-add-pick-btn"
                  onClick={() => addExtraPick(index)}
                >
                  + Adicionar seleção (Bet Builder)
                </button>
              </div>

              {game.extra_picks?.length > 0 && (
                <div className="bb-combined-row">
                  <span className="bb-combined-txt">
                    Odd combinada deste jogo &nbsp;
                    <b>
                      {[game.game_odd, ...game.extra_picks.map(p => p.odd)]
                        .filter(o => parseFloat(o))
                        .map(o => parseFloat(o).toFixed(2))
                        .join(' × ')}
                    </b>
                  </span>
                  <span className="bb-combined-odd">{getCombinedOdd(game).toFixed(2)}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="form-actions">
          <Link to="/backoffice/bets" className="btn-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn-primary" disabled={isSaving}>
            {isSaving ? 'A guardar...' : (isEditing ? 'Guardar Alterações' : 'Criar Bet')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BetForm;
